import { useState, useRef, useEffect } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { ChevronDown, Plus, Building2 } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { api } from '../lib/api'
import { useDemoMode } from '../hooks/usePreserveQueryParams'

export default function BrandSwitcher() {
  const { activeBrand, brands, switchBrand } = useAuth()
  const { isDemoMode } = useDemoMode()
  const [isOpen, setIsOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const navigate = useNavigate()

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  const handleBrandSwitch = async (brandId: string) => {
    try {
      // Find the brand being switched to (check both id and brandId fields)
      const brand = brands.find(b => (b.id || b.brandId) === brandId)
      if (!brand) {
        console.error('[BrandSwitcher] Brand not found:', brandId)
        return
      }

      // Use the proper ID (prefer id, fallback to brandId)
      const actualBrandId = brand.id || brand.brandId
      if (!actualBrandId) {
        console.error('[BrandSwitcher] Brand has no valid ID:', brand)
        return
      }

      // Fetch fresh brand data with subscription fields
      console.log('[BrandSwitcher] Fetching fresh brand data...')
      const brandsResponse = await api.get('/brands')
      const allBrands = brandsResponse.data.brands || []
      const freshBrand = allBrands.find((b: any) => (b.id || b.brandId) === actualBrandId)

      if (!freshBrand) {
        console.error('[BrandSwitcher] Could not fetch fresh brand data')
        // Fall back to old behavior if we can't get fresh data
        await switchBrand(actualBrandId)
        setIsOpen(false)
        return
      }

      // Check if brand has inactive subscription using FRESH data
      console.log('[BrandSwitcher] Checking subscription for brand:', {
        companyName: freshBrand.companyName,
        subscriptionProduct: freshBrand.subscriptionProduct,
        subscriptionStatus: freshBrand.subscriptionStatus,
        trialEndsAt: freshBrand.trialEndsAt
      })

      const isTrialExpired = () => {
        if (!freshBrand.trialEndsAt) return false
        const now = new Date()
        const trialEnd = new Date(freshBrand.trialEndsAt)
        return trialEnd.getTime() < now.getTime()
      }

      const hasInactiveSubscription = () => {
        // No subscription product and not on trial
        if (!freshBrand.subscriptionProduct && freshBrand.subscriptionStatus !== 'TRIAL') {
          console.log('[BrandSwitcher] Inactive: No product and not trialing')
          return true
        }
        // Subscription is canceled or unpaid
        if (freshBrand.subscriptionStatus === 'CANCELED' || freshBrand.subscriptionStatus === 'UNPAID') {
          console.log('[BrandSwitcher] Inactive: Canceled or unpaid')
          return true
        }
        // Trial has expired
        if (freshBrand.subscriptionStatus === 'TRIAL' && isTrialExpired()) {
          console.log('[BrandSwitcher] Inactive: Trial expired')
          return true
        }
        console.log('[BrandSwitcher] Subscription is active')
        return false
      }

      const isInactive = hasInactiveSubscription()

      // Always switch the brand first
      await switchBrand(actualBrandId)
      setIsOpen(false)

      // If brand has inactive subscription, redirect to settings with modal open
      if (isInactive) {
        console.log('[BrandSwitcher] Redirecting to settings for inactive brand')
        navigate(`/settings/brands?brandId=${actualBrandId}`, { replace: true })
      }
    } catch (error) {
      console.error('Failed to switch brand:', error)
    }
  }

  const handleAddBrand = () => {
    setIsOpen(false)
    // No brand limits - users can add unlimited brands
    // 1st brand gets trial, additional brands require plan selection
    navigate('/add-brand')
  }

  // In demo mode, show static Demo Brand (non-interactive)
  if (isDemoMode) {
    return (
      <div className="relative">
        <div className="w-full flex items-center space-x-2 px-4 py-2 bg-gray-100 border border-gray-300 rounded-lg cursor-not-allowed opacity-75">
          <Building2 className="h-5 w-5 text-gray-500" />
          <span className="w-full text-xs font-medium text-gray-700 text-left">
            Acme Snacks (DEMO)
          </span>
        </div>
      </div>
    )
  }

  if (!activeBrand && brands.length === 0) {
    return (
      <button
        onClick={handleAddBrand}
        className="flex items-center space-x-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
      >
        <Plus className="h-4 w-4" />
        <span>Add Your Brand</span>
      </button>
    )
  }

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center space-x-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
      >
        <Building2 className="h-5 w-5 text-gray-600" />
        <span className="w-full text-xs font-medium text-gray-900 text-left">
          {activeBrand?.companyName || 'Select Brand'}
        </span>
        <ChevronDown className={`h-4 w-4 text-gray-500 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="absolute left-0 mt-2 w-64 bg-white border border-gray-200 rounded-lg shadow-lg z-50">
          <div className="py-2">
            {brands.map((brand) => {
              const brandId = brand.id || brand.brandId
              const isActive = (activeBrand?.id || activeBrand?.brandId) === brandId
              return (
                <button
                  key={brandId}
                  onClick={() => handleBrandSwitch(brandId)}
                  className={`w-full text-left px-4 py-2 hover:bg-gray-50 transition-colors ${
                    isActive ? 'bg-indigo-50' : ''
                  }`}
                >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-900">{brand.companyName}</p>
                    {brand.domain && (
                      <p className="text-xs text-gray-500">{brand.domain}</p>
                    )}
                  </div>
                  <div className="text-right">
                    <span className="text-xs text-gray-500">AI Score</span>
                    <p className="text-sm font-semibold text-indigo-600">{brand.aiIndexScore.toFixed(1)}</p>
                  </div>
                </div>
              </button>
              )
            })}

            <div className="border-t border-gray-200 mt-2 pt-2">
              <button
                onClick={handleAddBrand}
                className="w-full text-left px-4 py-2 hover:bg-gray-50 transition-colors flex items-center space-x-2"
              >
                <Plus className="h-4 w-4 text-indigo-600" />
                <span className="text-sm font-medium text-indigo-600">Add New Brand</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}